import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';

/** How the people who close trips out answer a distance gap (gitaumoses4/chukua#109). */
export const DISTANCE_RESOLUTION_OUTCOMES = ['accepted', 'gps_adjusted', 'odometer_error_noted'] as const;
export type DistanceResolutionOutcome = (typeof DISTANCE_RESOLUTION_OUTCOMES)[number];

/** Why a trip is flagged: the gap is beyond tolerance, or a reading cannot be right. */
export const DISTANCE_GAP_REASON_CODES = ['beyond_tolerance', 'below_dispatch_reading', 'below_truck_odometer'] as const;
export type DistanceGapReasonCode = (typeof DISTANCE_GAP_REASON_CODES)[number];

/** Why there is nothing to compare: shown as "Not available" with this reason, never as 0. */
export const DISTANCE_NOT_COMPARABLE_CODES = ['no_dispatch_reading', 'no_close_out_reading', 'no_gps_distance', 'low_gps_coverage'] as const;
export type DistanceNotComparableCode = (typeof DISTANCE_NOT_COMPARABLE_CODES)[number];

export const DISTANCE_STATE_CODES = ['gap', 'resolved', 'agrees', 'awaiting_gps', 'not_comparable'] as const;
export type DistanceStateCode = (typeof DISTANCE_STATE_CODES)[number];

/** What the truck's odometer did with the close-out reading; null while it has done nothing. */
export type DistanceTruckWrite = 'written' | 'below_current' | null;

/** How long back a truck's record looks for trips with a gap. A constant, not a setting. */
export const TRUCK_DISTANCE_PERIOD_DAYS = 90;

/** `GET /distance-agreement/trips`: the closed-out trips with their comparison, the most recently closed first. */
export const distanceTripsQuerySchema = defineListQuery({
  sortFields: ['trip', 'closedOutAt', 'gapKm', 'gapPercent'],
  defaultSort: { sort: 'closedOutAt', order: 'desc' },
  filters: {
    state: listFilter(z.enum(DISTANCE_STATE_CODES)),
    truckId: listFilter(z.uuid()),
  },
});
export type DistanceTripsQuery = z.infer<typeof distanceTripsQuerySchema>;

/** `POST /distance-agreement/trips/:id/resolutions`. A note is required: the answer is only worth keeping with its reason. */
export const resolveDistanceGapSchema = z
  .object({
    outcome: z.enum(DISTANCE_RESOLUTION_OUTCOMES),
    note: z.string().trim().min(1, 'Say why').max(1000),
  })
  .strict();
export type ResolveDistanceGapInput = z.infer<typeof resolveDistanceGapSchema>;

export interface DistanceTripItem {
  tripId: string;
  /** `T-<n>`. */
  number: string;
  truckId: string;
  truckRegistration: string | null;
  /** Whole km; null with `notComparableReason` or a reading below the dispatch reading, never 0. */
  odometerDistanceKm: number | null;
  gpsDistanceKm: number | null;
  gpsCoveragePct: number | null;
  /** The GPS figure is a floor: shown, but it cannot flag. */
  isGpsLowCoverage: boolean;
  gapKm: number | null;
  gapPercent: number | null;
  state: DistanceStateCode;
  reason: DistanceGapReasonCode | null;
  notComparableReason: DistanceNotComparableCode | null;
  closedOutAt: string;
}

export interface DistanceReadingSource {
  km: number;
  at: string;
  /** The office user who recorded it; null when it came from the driver's phone. */
  byName: string | null;
  eventId: string;
}

export interface DistanceResolutionView {
  id: string;
  outcome: DistanceResolutionOutcome;
  note: string;
  resolvedByName: string | null;
  createdAt: string;
  /** The earlier resolution this one superseded. */
  correctsResolutionId: string | null;
  /** The reason the gap had when it was answered. */
  reason: DistanceGapReasonCode | null;
}

export interface DistanceJudgementHistoryEntry {
  id: string;
  judgedAt: string;
  gpsDistanceKm: number | null;
  gpsCoveragePct: number | null;
  odometerDistanceKm: number | null;
  gapKm: number | null;
  gapPercent: number | null;
  tolerancePercent: number;
  reason: DistanceGapReasonCode | null;
  notComparableReason: DistanceNotComparableCode | null;
}

/** `GET /distance-agreement/trips/:id`: the comparison with every source behind it. */
export interface DistanceTripDetail extends DistanceTripItem {
  dispatchReading: DistanceReadingSource | null;
  closeOutReading: DistanceReadingSource | null;
  /** When tracking computed the GPS figure. */
  gpsComputedAt: string | null;
  tolerancePercent: number;
  truckWrite: DistanceTruckWrite;
  /** The odometer distance once a resolver judged the GPS figure wrong; what fuel reconciliation reads. Null otherwise. */
  authoritativeDistanceKm: number | null;
  /** Newest first, superseded ones included. */
  resolutions: DistanceResolutionView[];
  /** Newest first: every figure the trip was judged on, including the GPS ones a resolution set aside. */
  history: DistanceJudgementHistoryEntry[];
}

/** `GET /distance-agreement/trucks/:id`: a truck's gap-flagged trips for the recent period. */
export interface TruckDistanceGaps {
  periodDays: number;
  count: number;
  /** The newest few; `count` is all of them. */
  items: DistanceTripItem[];
}
